import React, { useState, useEffect } from 'react'
import config from '../../config.js'
import { apiErrorMessage } from '../../utils/apiError';

const NESTER = config.NESTER_API_URL

/**
 * AI Coach panel — calls `/projects/{id}/ai_coach` to fetch Claude-generated
 * optimisation suggestions for the current nest, with Apply / Dismiss /
 * Explain actions on each card.
 *
 * The backend returns a list of suggestions shaped like:
 *   {
 *     id: 'rotate-part-17',
 *     severity: 'info' | 'warn' | 'win',
 *     title: 'Rotate BRKT-04 to 90°',
 *     body: 'Utilization rises from 73% to 82% for Group 1.',
 *     impact: { dollars: 0.31, util_delta_pct: 9 },
 *     action: { kind: 'patch_part', part_id: 17, patch: { part_rotation: 90 } }
 *   }
 */
export default function NesterAiCoach({
  colors,
  project,
  nestResult,
  onApplyAction,
  onSetSuggestionCount,
}) {
  const [running, setRunning] = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [error, setError] = useState(null)
  const [dismissed, setDismissed] = useState(new Set())
  const [lastRunAt, setLastRunAt] = useState(null)

  // Broadcast undismissed count to parent for tab badging
  useEffect(() => {
    const visible = suggestions.filter(s => !dismissed.has(s.id)).length
    onSetSuggestionCount?.(visible)
  }, [suggestions, dismissed, onSetSuggestionCount])

  async function runAnalysis() {
    if (!project?.project_id) return
    setRunning(true); setError(null)
    try {
      const res = await fetch(`${NESTER}/projects/${project.project_id}/ai_coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nest_id: nestResult?.nest_id || null,
          include_customer_history: true,
          include_material_stock: true,
        }),
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const data = await res.json()
      setSuggestions(data.suggestions || [])
      setLastRunAt(new Date())
    } catch (e) {
      setError(e.message || 'Failed to get suggestions')
    }
    setRunning(false)
  }

  const visible = suggestions.filter(s => !dismissed.has(s.id))

  return (
    <div style={{ padding: 16 }}>
      <div style={styles.header(colors)}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
            🧠 AI Coach
          </div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
            Claude analyzes your nest and suggests concrete improvements — rotation tweaks,
            grouping, margin vs. market, remnant matches, and more.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {lastRunAt && (
            <span style={{ fontSize: 11, color: colors.textMuted }}>
              Last run: {lastRunAt.toLocaleTimeString()}
            </span>
          )}
          <button onClick={runAnalysis} disabled={running || !project}
            style={styles.runBtn(colors, running)}>
            {running ? 'Analyzing…' : suggestions.length > 0 ? '↻ Re-analyze' : 'Analyze nest'}
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.error(colors)}>
          {error}
        </div>
      )}

      {suggestions.length === 0 && !running && !error && (
        <div style={styles.empty(colors)}>
          <div style={{ fontSize: 28, opacity: 0.5, marginBottom: 8 }}>💡</div>
          <div style={{ fontSize: 13, color: colors.textMuted }}>
            Click <strong>Analyze nest</strong> to get Claude's take on how to improve utilization,
            cost, or margin on this project.
          </div>
        </div>
      )}

      {running && (
        <div style={styles.empty(colors)}>
          <div style={{ fontSize: 13, color: colors.text }}>
            Claude is reviewing {project?.project_name}… this usually takes 10-20 seconds.
          </div>
        </div>
      )}

      {visible.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(s => (
            <SuggestionCard key={s.id}
              colors={colors}
              suggestion={s}
              onApply={async () => {
                if (s.action) {
                  await onApplyAction?.(s.action)
                  setDismissed(prev => new Set([...prev, s.id]))
                }
              }}
              onDismiss={() => setDismissed(prev => new Set([...prev, s.id]))}
            />
          ))}
        </div>
      )}

      {suggestions.length > 0 && dismissed.size > 0 && (
        <div style={{ marginTop: 14, fontSize: 12, color: colors.textMuted, textAlign: 'center' }}>
          {dismissed.size} suggestion{dismissed.size === 1 ? '' : 's'} dismissed ·{' '}
          <button onClick={() => setDismissed(new Set())}
            style={{ background: 'none', border: 'none', color: colors.accent, cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>
            Restore all
          </button>
        </div>
      )}
    </div>
  )
}

function SuggestionCard({ colors, suggestion, onApply, onDismiss }) {
  const [expanded, setExpanded] = useState(false)
  const sev = suggestion.severity || 'info'
  const sevColor = sev === 'win' ? colors.success : sev === 'warn' ? colors.warn : colors.accent
  const sevBg = sev === 'win' ? colors.successSoft : sev === 'warn' ? colors.warnSoft : colors.accentSoft

  const impact = suggestion.impact || {}

  return (
    <div style={{
      background: colors.panel,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${sevColor}`,
      borderRadius: 6,
      padding: '10px 12px',
      boxShadow: colors.shadow,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <span style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
          padding: '2px 8px', borderRadius: 10,
          background: sevBg, color: sevColor,
          flexShrink: 0, letterSpacing: '0.04em',
        }}>
          {sev === 'win' ? 'Big Win' : sev === 'warn' ? 'Warning' : 'Idea'}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
            {suggestion.title}
          </div>
          <div style={{ fontSize: 12, color: colors.text, marginTop: 3, lineHeight: 1.5 }}>
            {suggestion.body}
          </div>
          {(impact.dollars != null || impact.util_delta_pct != null || impact.time_delta_sec != null) && (
            <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12 }}>
              {impact.dollars != null && (
                <span style={{ color: impact.dollars >= 0 ? colors.success : colors.danger, fontWeight: 700 }}>
                  {impact.dollars >= 0 ? '▲' : '▼'} ${Math.abs(impact.dollars).toFixed(2)}/{impact.dollar_unit || 'part'}
                </span>
              )}
              {impact.util_delta_pct != null && (
                <span style={{ color: impact.util_delta_pct >= 0 ? colors.success : colors.danger, fontWeight: 700 }}>
                  {impact.util_delta_pct >= 0 ? '+' : ''}{impact.util_delta_pct}% util
                </span>
              )}
              {impact.time_delta_sec != null && (
                <span style={{ color: impact.time_delta_sec <= 0 ? colors.success : colors.danger, fontWeight: 700 }}>
                  {impact.time_delta_sec <= 0 ? '−' : '+'}{Math.abs(Math.round(impact.time_delta_sec / 60))}m
                </span>
              )}
            </div>
          )}
          {expanded && suggestion.explanation && (
            <div style={{
              marginTop: 8, padding: 10, borderRadius: 4,
              background: colors.panelAlt, border: `1px solid ${colors.border}`,
              fontSize: 12, color: colors.text, lineHeight: 1.6, whiteSpace: 'pre-wrap',
            }}>
              {suggestion.explanation}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          {suggestion.action && (
            <button onClick={onApply} style={styles.applyBtn(colors)}>Apply</button>
          )}
          {suggestion.explanation && (
            <button onClick={() => setExpanded(v => !v)} style={styles.ghostBtn(colors)}>
              {expanded ? 'Less' : 'Explain'}
            </button>
          )}
          <button onClick={onDismiss} style={styles.ghostBtn(colors)}>Dismiss</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  header: (c) => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '12px 14px', marginBottom: 14,
    background: c.panelAlt,
    border: `1px solid ${c.border}`,
    borderRadius: 6,
  }),
  runBtn: (c, disabled) => ({
    padding: '6px 14px', fontSize: 12, fontWeight: 700,
    border: 'none', borderRadius: 4,
    background: disabled ? c.panelAlt : c.accent,
    color: disabled ? c.textMuted : '#fff',
    cursor: disabled ? 'wait' : 'pointer',
    whiteSpace: 'nowrap',
  }),
  empty: (c) => ({
    padding: '40px 20px', textAlign: 'center',
    background: c.panel,
    border: `1px dashed ${c.border}`,
    borderRadius: 6,
  }),
  error: (c) => ({
    padding: 10, marginBottom: 12,
    background: c.dangerSoft,
    border: `1px solid ${c.danger}`,
    borderRadius: 4,
    color: c.danger,
    fontSize: 12,
  }),
  applyBtn: (c) => ({
    padding: '4px 12px', fontSize: 11, fontWeight: 700,
    background: c.accent, color: '#fff',
    border: 'none', borderRadius: 4, cursor: 'pointer',
  }),
  ghostBtn: (c) => ({
    padding: '4px 10px', fontSize: 11, fontWeight: 500,
    background: 'transparent', color: c.textMuted,
    border: `1px solid ${c.border}`, borderRadius: 4, cursor: 'pointer',
  }),
}
